import { RootState } from 'redux/store';

import { readBrandingImageValue } from 'helpers/brandingImage';

/**
 * The configured default timezone, or `null` when none is set or the value is
 * unusable.
 *
 * `config.items[].value` is typed `object` in the slice, and the
 * `default_timezone` row is seeded by a nap-backend migration as
 * `{"timezone": "UTC"}`. Two of the three shapes accepted here have never been
 * observed on the wire — they come from the tolerant parse the scheduling-window
 * form grew, and are kept because narrowing them is a behaviour change nobody has
 * verified is safe. The point of putting it here is that ONE place is tolerant,
 * instead of each consumer inventing its own reading.
 *
 * Note this does not validate that the string is a real IANA zone; the backend
 * does that on write.
 */
export const selectDefaultTimezone = (state: RootState): string | null => {
  const raw = state.config?.items?.find((config) => config.key === 'default_timezone')?.value as
    | string
    | { timezone?: string; default_timezone?: string; value?: string }
    | undefined;

  const candidate = typeof raw === 'string' ? raw : (raw?.timezone ?? raw?.default_timezone ?? raw?.value);
  const trimmed = typeof candidate === 'string' ? candidate.trim() : '';
  return trimmed === '' ? null : trimmed;
};

/**
 * The installation's branding image as a data URI, or null when none is set.
 *
 * Reads through the same tolerant reader the Branding editor writes with, so
 * the two cannot disagree about what an absent, null or malformed value means.
 * Anything that is not a data URI reads as "no image" rather than reaching an
 * `<img src>` — the row is editable through the API as free-form JSON, so a
 * value this selector has never seen is a normal possibility, not a bug.
 */
export const selectBrandingImage = (state: RootState): string | null =>
  readBrandingImageValue(state.config?.items?.find((config) => config.key === 'branding')?.value);
